import React, { useEffect, useState } from "react";
import { Sparkles, Copy as CopyIcon, Wand2, Languages } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { api } from "../../lib/api";

/**
 * Phase 22 — Marketing IA page.
 * Generates 3 Spanish variants (title / subtitle / CTA) for one of 4 channels.
 */
export default function AdminMarketing() {
  const [channel, setChannel] = useState("hero_slide");
  const [topic, setTopic] = useState("");
  const [extra, setExtra] = useState("");
  const [variants, setVariants] = useState([]);
  const [running, setRunning] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(null); // {text}

  const generate = async () => {
    if (!topic.trim()) return toast.error("Escribe un tema");
    setRunning(true);
    const t = toast.loading("IA generando variantes…");
    try {
      const { data } = await api.post("/admin/ai/marketing/generate", {
        channel,
        topic,
        extra_context: extra || null,
      });
      setVariants(data.variants || []);
      toast.success(`${(data.variants || []).length} variantes`, { id: t });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout"
          ? "IA tardó demasiado"
          : "IA no disponible",
        { id: t },
      );
    } finally {
      setRunning(false);
    }
  };

  const copy = async (obj) => {
    const text = `${obj.title}\n${obj.subtitle}\n[${obj.cta_label}]`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div data-testid="admin-marketing" className="space-y-4 max-w-4xl">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-[#00953B]" />
        <h1 className="text-3xl font-black tracking-tight">Marketing IA</h1>
      </div>
      <p className="text-sm text-neutral-500">
        Genera 3 variantes en español para tus canales de marketing.
      </p>
      <div className="rounded-2xl border bg-white p-5 space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label>Canal</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="marketing-channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hero_slide">Carrusel de home</SelectItem>
                <SelectItem value="whatsapp_broadcast">WhatsApp masivo</SelectItem>
                <SelectItem value="novedades_banner">Banner novedades</SelectItem>
                <SelectItem value="email_digest">Email periódico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tema</Label>
            <Input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Ej: Envíos gratis en Ramadán"
              data-testid="marketing-topic"
            />
          </div>
        </div>
        <div>
          <Label>Contexto extra (opcional)</Label>
          <Textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="Fechas, oferta específica, público objetivo…"
            rows={3}
          />
        </div>
        <Button
          onClick={generate}
          disabled={running}
          data-testid="marketing-generate-btn"
          className="bg-[#00953B] hover:bg-[#007a30] gap-1"
        >
          <Wand2 className={`h-4 w-4 ${running ? "animate-pulse" : ""}`} />
          Generar 3 variantes
        </Button>
      </div>
      {variants.length > 0 && (
        <div className="grid gap-3 md:grid-cols-3" data-testid="marketing-results">
          {variants.map((v, i) => (
            <div
              key={i}
              data-testid={`marketing-variant-${i}`}
              className="rounded-2xl border bg-white p-4 space-y-2"
            >
              <div className="text-xs uppercase text-neutral-400">Variante {i + 1}</div>
              <div className="font-bold text-lg leading-tight">{v.title}</div>
              <div className="text-sm text-neutral-700">{v.subtitle}</div>
              <div className="text-xs inline-block rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5">
                {v.cta_label}
              </div>
              <div className="flex gap-1 pt-2 border-t border-neutral-100">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid={`marketing-copy-${i}`}
                  onClick={() => copy(v)}
                  className="gap-1 rounded-full h-8 text-xs"
                >
                  <CopyIcon className="h-3 w-3" /> Copiar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`marketing-translate-${i}`}
                  onClick={() =>
                    setTranslateOpen({
                      text: `${v.title}\n${v.subtitle}\n[${v.cta_label}]`,
                    })
                  }
                  className="gap-1 rounded-full h-8 text-xs"
                >
                  <Languages className="h-3 w-3" /> Traducir
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {translateOpen && (
        <TranslateModal
          text={translateOpen.text}
          onClose={() => setTranslateOpen(null)}
        />
      )}
    </div>
  );
}

export function TranslateModal({ text, onClose }) {
  const [target, setTarget] = useState("ar");
  const [translated, setTranslated] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    const t = toast.loading("IA traduciendo…");
    try {
      const { data } = await api.post("/admin/ai/translate", { text, target });
      setTranslated(data.text || "");
      toast.success("Traducción lista", { id: t });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail === "ai_timeout"
          ? "IA tardó demasiado"
          : "IA no disponible",
        { id: t },
      );
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(translated);
      toast.success("Copiado");
    } catch {
      toast.error("No se pudo copiar");
    }
  };
  useEffect(() => {
    run();
    // eslint-disable-next-line
  }, [target]);
  return (
    <div
      role="dialog"
      aria-label="Traducción"
      data-testid="translate-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg">Traducir</h3>
          <button
            data-testid="translate-modal-close"
            onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-full hover:bg-neutral-100"
          >
            ×
          </button>
        </div>
        <div className="flex gap-2">
          {[
            { v: "ar", l: "AR" },
            { v: "fr", l: "FR" },
            { v: "en", l: "EN" },
          ].map((o) => (
            <Button
              key={o.v}
              size="sm"
              variant={target === o.v ? "default" : "outline"}
              onClick={() => setTarget(o.v)}
              data-testid={`translate-lang-${o.v}`}
              className={target === o.v ? "bg-[#00953B]" : ""}
            >
              {o.l}
            </Button>
          ))}
        </div>
        <div className="text-xs text-neutral-500">Original</div>
        <pre className="text-sm bg-neutral-50 p-2 rounded whitespace-pre-wrap">{text}</pre>
        <div className="text-xs text-neutral-500">
          Traducción {busy ? "(generando…)" : ""}
        </div>
        <pre
          data-testid="translate-modal-result"
          dir={target === "ar" ? "rtl" : "ltr"}
          className="text-sm bg-emerald-50 p-2 rounded whitespace-pre-wrap min-h-[3rem]"
        >
          {translated || (busy ? "" : "—")}
        </pre>
        <div className="flex justify-end gap-2 pt-2 border-t border-neutral-100">
          <Button size="sm" variant="outline" onClick={copy} disabled={!translated}>
            <CopyIcon className="h-3 w-3 mr-1" /> Copiar
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
